'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { IconName } from '@/components/ui/Icon';
import { useMenu } from '@/context/MenuContext';

import MenuAvatar from './MenuAvatar';
import {
  privacyPolicyItems,
  adminMenuItems,
  settingsItems,
} from './MenuConfig';
import type { MenuItemType } from './MenuConfig';
import MenuItem from './MenuItem';

export default function DesktopMenu({
  menuItems,
  userId,
}: {
  menuItems: MenuItemType[];
  userId: string;
}) {
  const { setHasMenu } = useMenu();
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const { data: session } = useSession();

  useEffect(() => {
    setHasMenu(true);
    return () => setHasMenu(false);
  }, [setHasMenu]);

  return (
    <nav className='@container-[size] sticky top-0 z-20 hidden h-screen w-[290px] shrink-0 flex-col bg-white md:flex'>
      <div className='mb-4 flex justify-center pt-11'>
        <Link href='/'>
          <Image
            src='/images/logo.svg'
            alt='Haven Logo'
            width={146}
            height={27}
          />
        </Link>
      </div>

      <MenuAvatar userId={userId} size={60} className='@tall:hidden mx-8' />
      <MenuAvatar
        userId={userId}
        size={110}
        className='@tall:flex @tall:md:flex-col hidden'
      />

      <ul className='grow space-y-2 overflow-y-auto px-4'>
        {menuItems.map((item) => (
          <li key={item.title}>
            <MenuItem item={item} isMobile={false} />
          </li>
        ))}

        {session?.user?.admin === true && (
          <li>
            <DropdownMenu
              open={isAdminDropdownOpen}
              onOpenChange={setIsAdminDropdownOpen}
            >
              <DropdownMenuTrigger asChild>
                <button className='w-full focus:outline-hidden'>
                  <MenuItem
                    item={{
                      icon: IconName.Filter,
                      title: 'admin',
                      link: '#',
                    }}
                    isMobile={false}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side='right'
                sideOffset={0}
                align='start'
                className='w-[300px] space-y-6 bg-white py-6'
                collisionPadding={17}
              >
                {adminMenuItems.map((item) => (
                  <DropdownMenuItem
                    key={item.title}
                    className='border-b-0 py-0'
                  >
                    <MenuItem
                      item={item}
                      isMobile={false}
                      onClose={() => setIsAdminDropdownOpen(false)}
                    />
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
        )}
      </ul>
      <div className='px-4 pb-10'>
        <div className='border-t pt-8'>
          <DropdownMenu
            open={isSettingsDropdownOpen}
            onOpenChange={setIsSettingsDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <button className='focus:outline-hidden'>
                <MenuItem
                  item={{
                    icon: IconName.Settings,
                    title: 'accountSettings',
                    link: '#',
                  }}
                  isMobile={false}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side='right'
              sideOffset={0}
              align='start'
              className='w-[300px] space-y-6 bg-white py-6'
              collisionPadding={17}
            >
              {settingsItems.map((item) => (
                <DropdownMenuItem key={item.title} className='border-b-0 py-0'>
                  <MenuItem
                    item={item}
                    isMobile={false}
                    onClose={() => setIsSettingsDropdownOpen(false)}
                    color={item.color}
                  />
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className='mx-auto my-6 w-[80%]' />
              {privacyPolicyItems.map((item) => (
                <DropdownMenuItem key={item.title} className='border-b-0 py-0'>
                  <MenuItem
                    item={item}
                    isMobile={false}
                    onClose={() => setIsSettingsDropdownOpen(false)}
                  />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
